"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/auth/use-auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { EmailChannelConfigInput, NotificationChannelView } from "@/hooks/notifications/use-notification-channels";

interface EmailChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present = edit; absent = create. */
  channel?: NotificationChannelView | null;
  onSave: (name: string, config: EmailChannelConfigInput) => Promise<void>;
}

interface SenderCandidate {
  email: string;
  label: string;
  isPlatformDefault: boolean;
  isDefault: boolean;
}

/** Sentinel Select value for "auto-resolve" (Radix Select forbids empty values). */
const AUTO_SENDER = "__auto__";

/** Split a recipients textarea (commas or newlines) into trimmed addresses. */
function parseRecipients(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((r) => r.trim())
    .filter(Boolean);
}

export function EmailChannelDialog({ open, onOpenChange, channel, onSave }: EmailChannelDialogProps) {
  const { fetchWithAuth } = useAuth();
  const [name, setName] = useState("");
  const [recipients, setRecipients] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [subjectPrefix, setSubjectPrefix] = useState("");
  const [candidates, setCandidates] = useState<SenderCandidate[]>([]);
  const [saving, setSaving] = useState(false);

  // Re-seed the form whenever the dialog opens for a (different) channel.
  useEffect(() => {
    if (!open) return;
    const cfg = (channel?.config ?? {}) as Record<string, unknown>;
    setName(channel?.name ?? "");
    setRecipients(Array.isArray(cfg.recipients) ? (cfg.recipients as string[]).join("\n") : "");
    setFromEmail(typeof cfg.from_email === "string" ? cfg.from_email : "");
    setSubjectPrefix(typeof cfg.subject_prefix === "string" ? cfg.subject_prefix : "");
  }, [open, channel]);

  // The API rejects `from_email` on a system channel — hide the picker rather than
  // let the user choose something that 400s on save.
  const isSystem = channel?.isSystem ?? false;

  // Load the resolvable sender candidates (same set the send path can resolve),
  // so the operator can only pin an address that will actually be honored.
  useEffect(() => {
    if (!open || isSystem) return;
    let cancelled = false;
    fetchWithAuth("/api/agents/handover/sender-candidates")
      .then((r) => (r.ok ? r.json() : { candidates: [] }))
      .then((d) => {
        if (!cancelled) setCandidates(d.candidates ?? []);
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isSystem, fetchWithAuth]);

  // Preserve a saved-but-now-unresolvable sender as a flagged option, so editing
  // an unrelated field doesn't silently drop it.
  const senderOptions = useMemo(() => {
    const list = candidates.map((c) => ({ value: c.email, label: c.label }));
    if (fromEmail && !candidates.some((c) => c.email === fromEmail)) {
      list.unshift({ value: fromEmail, label: `${fromEmail} (unavailable)` });
    }
    return list;
  }, [candidates, fromEmail]);

  const isEdit = Boolean(channel);
  const recipientList = parseRecipients(recipients);
  const canSave = name.trim().length > 0 && recipientList.length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(name.trim(), {
        recipients: recipientList,
        ...(isSystem ? {} : { from_email: fromEmail.trim() || null }),
        subject_prefix: subjectPrefix.trim() || null,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save channel");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isSystem ? "Edit system email channel" : isEdit ? "Edit email channel" : "New email channel"}
          </DialogTitle>
          <DialogDescription>
            {isSystem
              ? "Provided by Versafy — no inbox of your own to connect. It delivers your billing alerts, and you can select it for any agent's escalation notifications."
              : "Your team receives a standalone alert at these addresses when an agent hands a conversation over to a human."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="nc-email-name">Name</Label>
            <Input
              id="nc-email-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Support team"
              maxLength={100}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nc-email-recipients">Recipients</Label>
            <Textarea
              id="nc-email-recipients"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder={"ops@example.com\nlead@example.com"}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              <span>One per line or comma-separated (up to 10).</span> {recipientList.length} <span>address</span>
              <span>{recipientList.length === 1 ? "" : "es"}</span><span>.</span>
            </p>
          </div>

          {isSystem ? (
            <div className="space-y-1.5">
              <Label>Sender</Label>
              <p className="text-sm">Versafy platform address</p>
              <p className="text-xs text-muted-foreground">
                Fixed for this channel — that is what lets it work without connecting an inbox.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="nc-email-from">Sender</Label>
              <Select
                value={fromEmail || AUTO_SENDER}
                onValueChange={(v) => setFromEmail(v === AUTO_SENDER ? "" : v)}
              >
                <SelectTrigger id="nc-email-from">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO_SENDER}>Auto (your sending inbox → platform default)</SelectItem>
                  {senderOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Only your connected sending inboxes (and the platform default) can be pinned.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="nc-email-prefix">Subject prefix (optional)</Label>
            <Input
              id="nc-email-prefix"
              value={subjectPrefix}
              onChange={(e) => setSubjectPrefix(e.target.value)}
              placeholder="🔴 Action needed"
              maxLength={120}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? "Saving…" : isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
